import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { describeError, useResetPassword } from '../api';

const schema = z
  .object({
    password: z.string().min(10, 'Parola en az 10 karakter olmalı.'),
    passwordConfirm: z.string(),
  })
  .refine((values) => values.password === values.passwordConfirm, {
    message: 'Parolalar eşleşmiyor.',
    path: ['passwordConfirm'],
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const reset = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    if (!token) {
      return;
    }

    await reset.mutateAsync({ token, newPassword: values.password });

    // Resetting revokes every session, including this browser's, so the only
    // sensible next step is signing in with the new password. /login renders the
    // home page with the dialog open over it.
    await navigate('/login', { replace: true });
  });

  return (
    <div className="status-page">
      <img src="/gee-logo.png" alt="" width={881} height={519} className="status-page-mark" />
      <h1 className="text-2xl font-semibold tracking-tight text-surface-50">Yeni parola belirle</h1>

      {!token ? (
        <Alert tone="error" className="mt-8">
          Bu bağlantıda sıfırlama kodu yok. Sıfırlama e-postasındaki bağlantıyı kullanın.
        </Alert>
      ) : (
        <form
          onSubmit={(event) => void onSubmit(event)}
          className="mt-8 flex flex-col gap-4"
          noValidate
        >
          {reset.isError ? <Alert tone="error">{describeError(reset.error)}</Alert> : null}

          <Field
            label="Yeni parola"
            type="password"
            autoComplete="new-password"
            hint="En az 10 karakter."
            error={errors.password?.message}
            {...register('password')}
          />

          <Field
            label="Yeni parola (tekrar)"
            type="password"
            autoComplete="new-password"
            error={errors.passwordConfirm?.message}
            {...register('passwordConfirm')}
          />

          <Button type="submit" loading={reset.isPending}>
            Parolayı değiştir
          </Button>

          <p className="text-xs text-surface-500">
            Parolan değiştiğinde açık olan tüm oturumlar kapatılır.
          </p>
        </form>
      )}

      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="text-surface-400 hover:text-surface-200">
          Girişe dön
        </Link>
      </p>
    </div>
  );
}
